import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import QRCode from "qrcode";
import { Download, Printer } from "lucide-react";
import { Navbar } from "./Navbar";
import { Footer } from "./Footer";
import { usePageMeta } from "../hooks/usePageMeta";

const EASE = [0.16, 1, 0.3, 1];
const W = 1125;
const H = 675;
const SAFE = 60;

const NAVY = "#0B2545";
const FOG = "#F6F8FA";
const TECH = "#2176C7";
const BRIGHT = "#38bdf8";
const STEEL = "#3E5C76";
const MUTED = "#8CA0B3";

const inputCls =
  "w-full bg-transparent border-b border-steel/50 focus:border-tech-bright outline-none py-3 text-fog placeholder:text-fog/30 text-sm transition-colors duration-300";

const drawFront = (ctx, mono, { name, title, email, phone }) => {
  ctx.fillStyle = "#FFFFFF";
  ctx.fillRect(0, 0, W, H);
  ctx.textBaseline = "alphabetic";
  ctx.letterSpacing = "0px";

  const mh = 190;
  const mw = (mono.width / mono.height) * mh;
  ctx.drawImage(mono, SAFE, SAFE + 10, mw, mh);

  const tx = SAFE + mw + 50;
  ctx.fillStyle = NAVY;
  ctx.letterSpacing = "3px";
  ctx.font = "700 56px Outfit, Arial, sans-serif";
  ctx.fillText((name || "YOUR NAME").toUpperCase(), tx, SAFE + 86);
  ctx.fillStyle = STEEL;
  ctx.letterSpacing = "6px";
  ctx.font = "500 26px Outfit, Arial, sans-serif";
  ctx.fillText((title || "YOUR TITLE").toUpperCase(), tx, SAFE + 135);

  ctx.letterSpacing = "0px";
  ctx.fillStyle = TECH;
  ctx.fillRect(SAFE, 320, W - SAFE * 2, 3);

  ctx.fillStyle = NAVY;
  ctx.font = "400 30px Outfit, Arial, sans-serif";
  ctx.fillText(`${phone || "(253) 453-1754"}   ·   ${email || "you@northwesttechalliance.com"}`, SAFE, 398);
  ctx.fillStyle = TECH;
  ctx.fillText("northwesttechalliance.com", SAFE, 458);

  ctx.fillStyle = STEEL;
  ctx.letterSpacing = "5px";
  ctx.font = "500 22px Outfit, Arial, sans-serif";
  ctx.fillText("AI  ·  SOFTWARE  ·  INNOVATION", SAFE, 572);

  ctx.letterSpacing = "0px";
  ctx.fillStyle = MUTED;
  ctx.font = "400 18px Outfit, Arial, sans-serif";
  const legal = "Northwest Title Alliance, DBA Northwest Tech Alliance";
  ctx.fillText(legal, W - SAFE - ctx.measureText(legal).width, 600);
};

const SITE_URL = "https://www.northwesttechalliance.com/";

const vEsc = (s) => s.replace(/\\/g, "\\\\").replace(/;/g, "\\;").replace(/,/g, "\\,").replace(/\n/g, "\\n");

const buildVCard = ({ name, title, email, phone }) => {
  const fn = (name || "Your Name").trim();
  const parts = fn.split(/\s+/);
  const last = parts.length > 1 ? parts[parts.length - 1] : "";
  const first = parts.length > 1 ? parts.slice(0, -1).join(" ") : fn;
  return [
    "BEGIN:VCARD",
    "VERSION:3.0",
    `N:${vEsc(last)};${vEsc(first)};;;`,
    `FN:${vEsc(fn)}`,
    "ORG:Northwest Tech Alliance",
    title ? `TITLE:${vEsc(title)}` : "",
    phone ? `TEL;TYPE=WORK,VOICE:${vEsc(phone)}` : "",
    email ? `EMAIL;TYPE=WORK:${vEsc(email)}` : "",
    `URL:${SITE_URL}`,
    "END:VCARD",
  ]
    .filter(Boolean)
    .join("\r\n");
};

const loadQr = async (data) => {
  const qrData = await QRCode.toDataURL(data, {
    margin: 0,
    width: 330,
    errorCorrectionLevel: "M",
    color: { dark: NAVY, light: "#FFFFFF" },
  });
  return new Promise((res) => {
    const im = new Image();
    im.onload = () => res(im);
    im.src = qrData;
  });
};

const drawBack = (ctx, qr, scanLabel) => {
  ctx.fillStyle = NAVY;
  ctx.fillRect(0, 0, W, H);
  ctx.textBaseline = "alphabetic";
  ctx.letterSpacing = "0px";

  ctx.fillStyle = TECH;
  ctx.fillRect(Math.floor(W * 0.06), Math.floor(H * 0.12), 3, Math.floor(H * 0.76));

  const bx = SAFE + 40;
  const by = (H - 330) / 2;
  ctx.fillStyle = "#FFFFFF";
  if (ctx.roundRect) {
    ctx.beginPath();
    ctx.roundRect(bx - 18, by - 18, 366, 366, 10);
    ctx.fill();
  } else {
    ctx.fillRect(bx - 18, by - 18, 366, 366);
  }
  ctx.drawImage(qr, bx, by, 330, 330);

  ctx.fillStyle = BRIGHT;
  ctx.letterSpacing = "5px";
  ctx.font = "500 20px Outfit, Arial, sans-serif";
  ctx.fillText(scanLabel, bx + 165 - ctx.measureText(scanLabel).width / 2, by + 388);

  const tx = bx + 330 + 80;
  ctx.fillStyle = FOG;
  ctx.letterSpacing = "8px";
  ctx.font = "300 58px Outfit, Arial, sans-serif";
  ctx.fillText("NORTHWEST", tx, 245);
  ctx.fillText("TECH ALLIANCE", tx, 320);
  ctx.letterSpacing = "0px";
  ctx.fillStyle = TECH;
  ctx.fillRect(tx, 375, 320, 3);
  ctx.fillStyle = BRIGHT;
  ctx.font = "400 30px Outfit, Arial, sans-serif";
  ctx.fillText("northwesttechalliance.com", tx, 425);
  ctx.fillStyle = MUTED;
  ctx.letterSpacing = "5px";
  ctx.font = "500 22px Outfit, Arial, sans-serif";
  ctx.fillText("AI  ·  SOFTWARE  ·  INNOVATION", tx, 482);
  ctx.letterSpacing = "0px";
};

const useCardCanvases = (form, qrMode) => {
  const [ready, setReady] = useState(0);
  const frontRef = useRef(null);
  const backRef = useRef(null);
  const monoRef = useRef(null);

  useEffect(() => {
    const im = new Image();
    im.onload = () => { monoRef.current = im; setReady((r) => r + 1); };
    im.src = "/assets/nwta-monogram.png";
    document.fonts?.ready.then(() => setReady((r) => r + 1));
  }, []);

  useEffect(() => {
    const fc = frontRef.current?.getContext("2d");
    if (fc && monoRef.current) drawFront(fc, monoRef.current, form);
  }, [form, ready]);

  useEffect(() => {
    let cancelled = false;
    const t = setTimeout(() => {
      const data = qrMode === "vcard" ? buildVCard(form) : SITE_URL;
      loadQr(data).then((qr) => {
        if (cancelled) return;
        const bc = backRef.current?.getContext("2d");
        if (bc) drawBack(bc, qr, qrMode === "vcard" ? "SCAN TO SAVE CONTACT" : "SCAN TO VISIT");
      });
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [form, qrMode, ready]);

  return { frontRef, backRef };
};

const downloadCanvas = (ref, filename) => {
  ref.current?.toBlob((blob) => {
    if (!blob) return;
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    a.click();
    URL.revokeObjectURL(a.href);
  }, "image/png");
};

const Field = ({ id, label, value, onChange, placeholder, type = "text" }) => (
  <div>
    <label htmlFor={id} className="font-mono-accent text-[10px] tracking-[0.25em] uppercase text-fog/40">{label}</label>
    <input id={id} data-testid={`${id}-input`} type={type} value={value} onChange={onChange} placeholder={placeholder} className={inputCls} />
  </div>
);

const qrModeBtnCls = (active) =>
  `px-4 py-2.5 font-mono-accent text-[10px] tracking-[0.2em] uppercase border transition-colors duration-300 ${
    active
      ? "border-tech-bright bg-tech/20 text-tech-bright"
      : "border-steel/50 text-fog/50 hover:border-steel hover:text-fog/80"
  }`;

const QrModeToggle = ({ qrMode, setQrMode }) => (
  <div>
    <span className="font-mono-accent text-[10px] tracking-[0.25em] uppercase text-fog/40">QR Code Content</span>
    <div className="flex flex-wrap gap-3 pt-3" data-testid="qr-mode-toggle">
      <button onClick={() => setQrMode("vcard")} data-testid="qr-mode-vcard" className={qrModeBtnCls(qrMode === "vcard")}>
        Save contact (vCard)
      </button>
      <button onClick={() => setQrMode("url")} data-testid="qr-mode-url" className={qrModeBtnCls(qrMode === "url")}>
        Website link
      </button>
    </div>
    <p className="text-xs text-fog/40 leading-relaxed pt-3">
      {qrMode === "vcard"
        ? "Scanning saves your name, title, phone, and email straight to their contacts."
        : "Scanning opens northwesttechalliance.com."}
    </p>
  </div>
);

const CardPreview = ({ label, canvasRef, ariaLabel, testId }) => (
  <div>
    <p className="font-mono-accent text-[10px] tracking-[0.3em] uppercase text-fog/40 mb-3">{label}</p>
    <div className="border border-steel/30 overflow-hidden" data-testid={testId}>
      <canvas ref={canvasRef} width={W} height={H} role="img" aria-label={ariaLabel} className="w-full h-auto block" />
    </div>
  </div>
);

const CardHeader = () => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.9, ease: EASE }}
    className="mb-14 md:mb-16"
  >
    <span className="font-mono-accent text-[11px] tracking-[0.35em] uppercase text-tech-bright">Team Tool</span>
    <h1 className="font-display font-bold tracking-[-0.03em] leading-[1.02] text-4xl sm:text-5xl lg:text-6xl text-fog mt-6">
      Business cards<span className="text-tech">.</span>
      <br />
      <span className="text-fog/40">Personalized in seconds.</span>
    </h1>
  </motion.div>
);

const PrintNote = () => (
  <p className="text-xs text-fog/40 leading-relaxed pt-2">
    <Printer className="h-3.5 w-3.5 inline mr-1.5 -mt-0.5" />
    Print-ready 3.5×2 in at 300 DPI with bleed. Upload both PNGs to MOO
    or Vistaprint.{" "}
    <a href="/assets/downloads/nwta-business-cards.zip" data-testid="card-kit-link" className="text-tech-bright hover:text-fog transition-colors duration-300">
      Download the blank print kit
    </a>{" "}
    for the universal front and full print instructions.
  </p>
);

const BusinessCardGenerator = () => {
  usePageMeta({ title: "Business Card Generator", description: "Create a print-ready Northwest Tech Alliance business card with a contact-saving QR code.", path: "/card-generator" });
  const [form, setForm] = useState({ name: "", title: "", email: "", phone: "(253) 453-1754" });
  const [qrMode, setQrMode] = useState("vcard");
  const { frontRef, backRef } = useCardCanvases(form, qrMode);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const slug = (form.name || "card").toLowerCase().replace(/[^a-z0-9]+/g, "-");
  const backFilename = qrMode === "vcard" ? `nwta-card-back-vcard-${slug}.png` : "nwta-card-back-qr.png";

  return (
    <main className="bg-navy min-h-screen">
      <Navbar />
      <section className="relative pt-36 md:pt-44 pb-24 md:pb-32 grain" data-testid="card-generator">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12">
          <CardHeader />

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.15, ease: EASE }}
              className="lg:col-span-4"
            >
              <div className="grid grid-cols-1 gap-7" data-testid="card-form">
                <Field id="card-name" label="Full Name" value={form.name} onChange={set("name")} placeholder="Jordan Hayes" />
                <Field id="card-title" label="Job Title" value={form.title} onChange={set("title")} placeholder="Account Executive" />
                <Field id="card-email" label="Email" type="email" value={form.email} onChange={set("email")} placeholder="jordan@northwesttechalliance.com" />
                <Field id="card-phone" label="Phone" type="tel" value={form.phone} onChange={set("phone")} />

                <QrModeToggle qrMode={qrMode} setQrMode={setQrMode} />

                <div className="flex flex-wrap gap-4 pt-3">
                  <button
                    onClick={() => downloadCanvas(frontRef, `nwta-card-front-${slug}.png`)}
                    data-testid="card-download-front"
                    className="group relative px-6 py-3.5 bg-tech text-fog font-mono-accent text-[11px] tracking-[0.22em] uppercase overflow-hidden"
                  >
                    <span className="absolute inset-0 bg-tech-bright translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-out" />
                    <span className="relative flex items-center gap-2"><Download className="h-4 w-4" /> Front PNG</span>
                  </button>
                  <button
                    onClick={() => downloadCanvas(backRef, backFilename)}
                    data-testid="card-download-back"
                    className="px-6 py-3.5 border border-steel text-fog/80 hover:border-tech-bright hover:text-tech-bright font-mono-accent text-[11px] tracking-[0.22em] uppercase transition-colors duration-300 flex items-center gap-2"
                  >
                    <Download className="h-4 w-4" /> Back PNG
                  </button>
                </div>

                <PrintNote />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.25, ease: EASE }}
              className="lg:col-span-8 space-y-8"
            >
              <CardPreview
                label="Front"
                canvasRef={frontRef}
                testId="card-front-preview"
                ariaLabel="Business card front preview with your name, title, and contact details"
              />
              <CardPreview
                label={qrMode === "vcard" ? "Back — QR saves your contact to their phone" : "Back — QR to northwesttechalliance.com"}
                canvasRef={backRef}
                testId="card-back-preview"
                ariaLabel={qrMode === "vcard" ? "Business card back preview with QR code that saves your contact details" : "Business card back preview with QR code linking to northwesttechalliance.com"}
              />
            </motion.div>
          </div>
        </div>
      </section>
      <Footer />
    </main>
  );
};

export default BusinessCardGenerator;
